import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../hooks/useNavigateBack';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import { ChartTrendingUpIcon, PencilIcon } from '../../components/ui/Icons';
import CrewFileTabs from './CrewFileTabs';
import { CrewIdentityHeader } from './CrewIdentityHeader';
import { crewFileToForm, emptyCrewForm, type CrewFormState } from './formMappers';
import { moduleConfig } from './config';
import { useCrewFile, useCrewLookups } from './useData';

const FileView: React.FC = () => {
  const { colors } = useTheme();
  const { id } = useParams<{ id: string }>();
  const crewId = id ? Number(id) : undefined;
  const goBack = useNavigateBack(
    crewId ? moduleConfig.module.route.detail(crewId) : moduleConfig.module.route.list,
  );
  const { data: item, isLoading } = useCrewFile(crewId);
  const { lookups, isLoading: loadingLookups } = useCrewLookups();
  const [form, setForm] = React.useState<CrewFormState>(emptyCrewForm);

  React.useEffect(() => {
    if (item) setForm(crewFileToForm(item));
  }, [item]);

  if (isLoading || loadingLookups) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading...
      </div>
    );
  }

  if (!item) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Crew file not found.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 -mt-1">
      <div
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <CrewIdentityHeader
          item={item}
          embedded
          onBack={goBack}
          backTitle="Back to dashboard"
          actions={
            <>
              <Link to={moduleConfig.module.route.detail(item.id)}>
                <Button type="button" variant="outline" size="sm">
                  <ChartTrendingUpIcon className="h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
              <Link to={moduleConfig.module.route.edit(item.id)}>
                <Button type="button" variant="accent" size="sm">
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            </>
          }
        />

        <CrewFileTabs
          form={form}
          setForm={setForm}
          lookups={lookups}
          readOnly
          crewId={item.id}
          documents={item.documents}
        />
      </div>
    </div>
  );
};

export default FileView;
